import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { Container, Paper, Typography, Box, Skeleton, Tabs, Tab, Button, Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import { HistoryOutlined, NoteAddOutlined, SaveOutlined } from '@mui/icons-material';

import { QK } from '../../../base/qk';
import { fetchAssessmentSchema, submitAssessmentResponse } from '../../../api/services/Assessment/InitalAssessment';
import useClientDetails from '../../../hooks/useClientDetails';
import { AssessmentSection, FormHeader } from '../../../components/CarePlan/InitialAssessment/Form';
import AssessmentHistory from '../../../components/Clients/CarePlan/InitialAssessments/AssessmentHistory';
import SvgSignatureField from '../../../components/SignaturePad/SvgSignatureField';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';

const AssessmentManager = () => {
  const { clientID, typeId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState(null);

  const { fullName } = useClientDetails(clientID);
  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      assessment_signature: null 
    }
  });

  const { data: schemaData, isLoading: isSchemaLoading } = useQuery(
    [QK.assessmentSchema, clientID, typeId], 
    () => fetchAssessmentSchema(clientID, typeId)
  );

  const { mutate, isLoading: isSubmitting } = useMutation(submitAssessmentResponse, {
    onSuccess: () => {
      enqueueSnackbar("Clinical assessment saved successfully!", { variant: 'success' });
      queryClient.invalidateQueries([QK.initAssessmentResponses, clientID, typeId]);
      reset();
      setShowConfirm(false);
      setActiveTab(1);
    },
    onError: (error) => {
      enqueueSnackbar(error?.error || "Error saving record", { variant: 'error' });
      setShowConfirm(false);
    }
  });

  const handlePreSubmit = (formData) => {
    const sectionWiseResponses = schemaData?.schema?.sections?.map((section) => ({
      section_title: section.section_title,
      observations: section.questions.map((q) => ({
        question_id: q.id,
        question_text: q.question_text,
        response_value: formData[`question_${q.id}`] || null,
        input_type: q.type
      }))
    }));

    setPendingData({
      client_id: clientID,
      type_id: typeId,
      form_responses: sectionWiseResponses,
      signature: formData.assessment_signature
    });
    
    setShowConfirm(true);
  };

  // ২. মোডাল থেকে কনফার্ম করলে মিউটেশন কল হবে
  const handleConfirmSave = () => {
    if (pendingData) {
      mutate(pendingData);
    }
  };

  if (isSchemaLoading) return (
    <Container sx={{ py: 6 }}>
      <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '24px' }} />
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <FormHeader
        title={schemaData?.type_name || "Care Assessment"} 
        fullName={fullName} 
        onBack={() => navigate(-1)} 
      />

      {/* Tabs and Content logic continues... */}
      <Box sx={{ borderBottom: 1, borderColor: '#EAECF0', mb: 5 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, val) => setActiveTab(val)}
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 } }}
        >
          <Tab icon={<NoteAddOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label="New Assessment" />
          <Tab icon={<HistoryOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label="Audit History" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <Box component="form" onSubmit={handleSubmit(handlePreSubmit)}>
          {schemaData?.schema?.sections?.map((section, index) => (
            <AssessmentSection
              key={index} 
              index={index} 
              section={section} 
              control={control} 
              errors={errors} 
            />
          ))}

          <Paper variant="outlined" sx={{ p: 4, mt: 4, borderRadius: '20px', bgcolor: '#FCFCFD', border: '1px solid #EAECF0' }}>
            <Typography variant="h6" fontWeight={800} color="#1D2939" mb={3}> Authorization & Declaration </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} md={7}>
                <Typography variant="body2" color="#475467" mb={2}>
                  I confirm that the information provided in this assessment is accurate to the best of my professional knowledge...
                </Typography>
                <Controller
                  name="assessment_signature"
                  control={control}
                  rules={{ required: "Signature is required for clinical authorization" }}
                  render={({ field }) => (
                    <SvgSignatureField label="Assessor Signature" value={field.value} onChange={field.onChange} />
                  )}
                />
                {errors.assessment_signature && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', fontWeight: 600 }}>
                    {errors.assessment_signature.message}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>
          
          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid #EAECF0', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderRadius: '12px', px: 4, fontWeight: 700, textTransform: 'none' }}> Cancel </Button>
            <Button 
              type="submit" 
              variant="contained" 
              startIcon={<SaveOutlined />}
              sx={{ borderRadius: '12px', px: 6, bgcolor: '#101828', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#1d2939' } }}
            >
              Authorize & Save
            </Button>
          </Box>
        </Box>
      )}

      {activeTab === 1 && <AssessmentHistory clientID={clientID} typeId={typeId} />}

      <ConfirmModal
        show={showConfirm}
        handleClose={() => setShowConfirm(false)}
        handleConfirm={handleConfirmSave}
        title="Finalize Assessment"
        body={`Are you sure you want to authorize and save this ${schemaData?.type_name || 'assessment'}? This will be recorded as a new clinical review cycle.`}
        isLoading={isSubmitting}
        confirmText="Yes, Authorize"
        cancelText="Review Again"
      />

      <Paper variant="outlined" sx={{ p: 2.5, mt: 8, borderRadius: '16px', bgcolor: '#F9FAF9', border: '1px dashed #D0D5DD', textAlign: 'center' }}>
        <Typography variant="body2" color="#667085" fontWeight={600}>
          Care Plan Initial Assessment Framework • UK Standard
        </Typography>
      </Paper>
    </Container>
  );
};

export default AssessmentManager;